import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import {
  Ship, ExternalLink, Loader2, Search, RefreshCw, CheckCircle2, XCircle,
  AlertTriangle, Copy, ShieldCheck, Globe, KeyRound, Info, Github, Cloud, BookOpen,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLES = {
  live: "border-emerald-500/40 text-emerald-400 bg-emerald-500/5",
  deploying: "border-yellow-500/40 text-yellow-400 bg-yellow-500/5",
  down: "border-red-500/40 text-red-400 bg-red-500/5",
  draft: "border-white/10 text-gray-400 bg-white/5",
  unknown: "border-white/10 text-gray-500 bg-transparent",
};

const STATUS_OPTIONS = ["unknown", "live", "deploying", "down", "draft"];

function StatusPill({ status }) {
  return (
    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 border ${STATUS_STYLES[status] || STATUS_STYLES.unknown}`}>
      {status}
    </span>
  );
}

function CategoryChip({ value, active, onClick, count }) {
  return (
    <button
      onClick={onClick}
      data-testid={`fleet-category-${value || "all"}`}
      className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] font-bold border transition-all ${
        active
          ? "border-[#C9A227] gold-text bg-[#C9A227]/5"
          : "border-white/10 text-gray-400 hover:border-white/30 hover:text-white"
      }`}
    >
      {value || "All"} {count !== undefined && <span className="ml-1 opacity-60">{count}</span>}
    </button>
  );
}

function EditDialog({ app, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    display_name: app.display_name || "",
    category: app.category || "",
    subdomain: app.subdomain || "",
    preview_url: app.preview_url || "",
    production_url: app.production_url || "",
    status: app.status || "unknown",
    notes: app.notes || "",
    env_secrets_set: !!app.env_secrets_set,
    domain_linked: !!app.domain_linked,
    github_repo: app.github_repo || "",
    github_branch: app.github_branch || "",
  }));
  const [saving, setSaving] = useState(false);

  const save = async (extra = {}) => {
    setSaving(true);
    try {
      const updated = await api.updateFleetApp(app.id, { ...form, ...extra });
      onSaved(updated);
      toast.success("Hull updated", { description: updated.display_name });
      onClose();
    } catch (err) {
      toast.error("Update failed", { description: err.response?.data?.detail || err.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="fleet-edit-dialog"
    >
      <div
        className="glass-strong w-full max-w-2xl max-h-[90vh] overflow-y-auto p-8 space-y-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <div className="label-tag gold-text">Edit Hull</div>
          <h3 className="text-2xl font-black tracking-tighter mt-1">{app.display_name}</h3>
          <div className="font-mono text-[10px] text-gray-500 mt-1">{app.job_id}</div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="label-tag mb-2">Display Name</div>
            <input
              className="w-full bg-black/50 border border-white/10 px-3 py-2 text-sm focus:border-[#C9A227] focus:outline-none rounded-none"
              value={form.display_name}
              onChange={(e) => setForm({ ...form, display_name: e.target.value })}
              data-testid="fleet-edit-display-name"
            />
          </div>
          <div>
            <div className="label-tag mb-2">Category</div>
            <input
              className="w-full bg-black/50 border border-white/10 px-3 py-2 text-sm focus:border-[#C9A227] focus:outline-none rounded-none"
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              data-testid="fleet-edit-category"
            />
          </div>
          <div>
            <div className="label-tag mb-2">Subdomain</div>
            <input
              placeholder="e.g. legal.eigooinc.net"
              className="w-full bg-black/50 border border-white/10 px-3 py-2 text-sm focus:border-[#C9A227] focus:outline-none rounded-none"
              value={form.subdomain}
              onChange={(e) => setForm({ ...form, subdomain: e.target.value })}
              data-testid="fleet-edit-subdomain"
            />
          </div>
          <div>
            <div className="label-tag mb-2">Status</div>
            <select
              className="w-full bg-black/50 border border-white/10 px-3 py-2 text-sm focus:border-[#C9A227] focus:outline-none rounded-none"
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
              data-testid="fleet-edit-status"
            >
              {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <div className="label-tag mb-2">Preview URL</div>
            <input
              placeholder="https://...preview.emergentagent.com"
              className="w-full bg-black/50 border border-white/10 px-3 py-2 text-sm focus:border-[#C9A227] focus:outline-none rounded-none"
              value={form.preview_url}
              onChange={(e) => setForm({ ...form, preview_url: e.target.value })}
              data-testid="fleet-edit-preview-url"
            />
          </div>
          <div>
            <div className="label-tag mb-2">Production URL</div>
            <input
              placeholder="https://your-hull.eigooinc.net"
              className="w-full bg-black/50 border border-white/10 px-3 py-2 text-sm focus:border-[#C9A227] focus:outline-none rounded-none"
              value={form.production_url}
              onChange={(e) => setForm({ ...form, production_url: e.target.value })}
              data-testid="fleet-edit-production-url"
            />
          </div>
        </div>

        <div>
          <div className="label-tag mb-2">Notes</div>
          <textarea
            rows={3}
            className="w-full bg-black/50 border border-white/10 px-3 py-2 text-sm focus:border-[#C9A227] focus:outline-none rounded-none"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            data-testid="fleet-edit-notes"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-white/10 pt-5">
          <div>
            <div className="label-tag mb-2 flex items-center gap-2">
              <Github className="w-3 h-3" /> GitHub Repo (owner/repo)
            </div>
            <input
              placeholder="eigooinc/api-armory"
              className="w-full bg-black/50 border border-white/10 px-3 py-2 text-sm focus:border-[#C9A227] focus:outline-none rounded-none font-mono"
              value={form.github_repo}
              onChange={(e) => setForm({ ...form, github_repo: e.target.value })}
              data-testid="fleet-edit-github-repo"
            />
          </div>
          <div>
            <div className="label-tag mb-2">GitHub Branch</div>
            <input
              placeholder="main"
              className="w-full bg-black/50 border border-white/10 px-3 py-2 text-sm focus:border-[#C9A227] focus:outline-none rounded-none font-mono"
              value={form.github_branch}
              onChange={(e) => setForm({ ...form, github_branch: e.target.value })}
              data-testid="fleet-edit-github-branch"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
          <label className="flex items-center gap-3 p-3 border border-white/10 cursor-pointer hover:border-white/30">
            <input
              type="checkbox"
              checked={form.env_secrets_set}
              onChange={(e) => setForm({ ...form, env_secrets_set: e.target.checked })}
              data-testid="fleet-edit-env-set"
            />
            <div>
              <div className="text-sm font-bold flex items-center gap-2"><KeyRound className="w-3.5 h-3.5 gold-text" /> Env secrets set</div>
              <div className="text-[10px] text-gray-500">Stripe / Airtable / etc pasted in the hull's env panel</div>
            </div>
          </label>
          <label className="flex items-center gap-3 p-3 border border-white/10 cursor-pointer hover:border-white/30">
            <input
              type="checkbox"
              checked={form.domain_linked}
              onChange={(e) => setForm({ ...form, domain_linked: e.target.checked })}
              data-testid="fleet-edit-domain-linked"
            />
            <div>
              <div className="text-sm font-bold flex items-center gap-2"><Globe className="w-3.5 h-3.5 gold-text" /> Custom domain linked</div>
              <div className="text-[10px] text-gray-500">Entri DNS configured, A-records removed</div>
            </div>
          </label>
        </div>

        <div className="flex items-center justify-between gap-3 pt-4 border-t border-white/10">
          <button onClick={onClose} className="text-xs label-tag hover:text-white" data-testid="fleet-edit-cancel">
            Cancel
          </button>
          <div className="flex items-center gap-3">
            <button
              onClick={() => save({ mark_deployed: true })}
              disabled={saving}
              className="btn-ghost-gold inline-flex items-center gap-2 text-xs"
              data-testid="fleet-edit-mark-deployed"
            >
              <CheckCircle2 className="w-3.5 h-3.5" /> Mark Deployed
            </button>
            <button
              onClick={() => save()}
              disabled={saving}
              className="btn-gold inline-flex items-center gap-2"
              data-testid="fleet-edit-save"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Fleet() {
  const [apps, setApps] = useState([]);
  const [stats, setStats] = useState(null);
  const [gh, setGh] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [reseeding, setReseeding] = useState(false);
  const [backingUp, setBackingUp] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [a, s, g] = await Promise.all([
        api.listFleetApps(),
        api.fleetStats(),
        api.githubStatus(),
      ]);
      setApps(a);
      setStats(s);
      setGh(g);
    } catch (err) {
      toast.error("Failed to load fleet", { description: err.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const categories = useMemo(() => {
    const map = {};
    apps.forEach((a) => { map[a.category] = (map[a.category] || 0) + 1; });
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [apps]);

  const filtered = useMemo(() => {
    return apps.filter((a) => {
      if (category && a.category !== category) return false;
      if (statusFilter && a.status !== statusFilter) return false;
      if (query) {
        const q = query.toLowerCase();
        if (!a.display_name.toLowerCase().includes(q) &&
            !a.name.toLowerCase().includes(q) &&
            !a.job_id.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [apps, category, statusFilter, query]);

  const reseed = async () => {
    setReseeding(true);
    try {
      const r = await api.seedFleet();
      toast.success(`Fleet seeded`, { description: `${r.inserted} new · ${r.total} total` });
      await load();
    } catch (err) {
      toast.error("Seed failed", { description: err.message });
    } finally {
      setReseeding(false);
    }
  };

  const backup = async () => {
    setBackingUp(true);
    try {
      const r = await api.fleetBackup();
      toast.success("Fleet snapshot committed", {
        description: `${r.commit_sha?.slice(0, 7)} · ${r.fleet_count} hulls`,
      });
    } catch (err) {
      toast.error("Backup failed", { description: err.response?.data?.detail || err.message });
    } finally {
      setBackingUp(false);
    }
  };

  const copyJobId = (jobId) => {
    navigator.clipboard.writeText(jobId);
    toast.success("Job ID copied");
  };

  return (
    <div className="space-y-10" data-testid="fleet-page">
      <section>
        <div className="flex items-center gap-3 mb-3">
          <Ship className="w-5 h-5 gold-text" strokeWidth={1.5} />
          <span className="label-tag">Armada Command Console</span>
        </div>
        <h1 className="text-4xl sm:text-5xl font-black tracking-tighter">
          The Whole <span className="gold-text">Fleet</span>, One Bridge.
        </h1>
        <p className="text-gray-400 mt-3 max-w-3xl">
          Every hull, every job ID, every subdomain — registered, categorized, and one click from its
          hosting dashboard. Redeploys, secret rotation, and domain linking remain manual operations
          inside each hull's own hosting panel (platform constraint).
        </p>
      </section>

      <div className="glass p-4 flex items-start gap-3 text-sm" data-testid="fleet-platform-notice">
        <Info className="w-4 h-4 gold-text flex-shrink-0 mt-0.5" strokeWidth={1.8} />
        <div className="text-gray-300 leading-relaxed">
          <strong className="text-white">Platform note:</strong> The hosting platform does not expose a cross-app
          deploy API or cross-app secret push. This console is a registry +
          checklist that deep-links you to each hull's dashboard — manual actions happen there.
        </div>
      </div>

      {/* GITHUB CONDUIT */}
      <div
        className={`glass p-4 flex flex-wrap items-center justify-between gap-4 ${
          gh?.is_live && gh?.authenticated ? "border-emerald-500/30" : "border-yellow-500/30"
        }`}
        data-testid="github-conduit-banner"
      >
        <div className="flex items-center gap-4">
          <div className={`w-10 h-10 flex items-center justify-center border ${
            gh?.is_live && gh?.authenticated ? "border-emerald-500/40" : "border-yellow-500/40"
          }`}>
            <Github className={`w-5 h-5 ${gh?.is_live && gh?.authenticated ? "text-emerald-400" : "text-yellow-400"}`} strokeWidth={1.5} />
          </div>
          <div>
            <div className="label-tag">GitHub Arsenal</div>
            <div className="text-sm font-bold mt-1">
              {!gh ? "Probing conduit…" :
                gh.is_live && gh.authenticated ? (
                  <span className="text-emerald-400">CONDUIT LIVE · @{gh.user?.login}</span>
                ) : gh.is_live ? (
                  <span className="text-yellow-400">PAT set but authentication failed</span>
                ) : (
                  <span className="text-yellow-400">STANDBY · drop GITHUB_PAT into backend/.env</span>
                )}
            </div>
            <div className="text-[10px] text-gray-500 mt-0.5">
              Backup target:{" "}
              {gh?.backup_owner && gh?.backup_repo ? (
                <span className="font-mono gold-text">{gh.backup_owner}/{gh.backup_repo}</span>
              ) : (
                <span className="text-gray-600">unconfigured (set GITHUB_BACKUP_OWNER + GITHUB_BACKUP_REPO)</span>
              )}
              {" · "}journals: {stats?.github_bound ?? 0} of {stats?.total ?? 0} hulls bound
            </div>
          </div>
        </div>
        <button
          onClick={backup}
          disabled={backingUp || !gh?.is_live || !gh?.backup_owner || !gh?.backup_repo}
          className="btn-gold inline-flex items-center gap-2 disabled:opacity-40"
          data-testid="fleet-backup-btn"
        >
          {backingUp ? <Loader2 className="w-4 h-4 animate-spin" /> : <Cloud className="w-4 h-4" />}
          Backup Fleet Now
        </button>
      </div>

      {/* STATS */}
      <section className="grid grid-cols-2 md:grid-cols-6 gap-4" data-testid="fleet-stats">
        {[
          { k: "total", label: "Total Hulls", val: stats?.total ?? "—" },
          { k: "live", label: "Live", val: stats?.by_status?.live ?? 0, color: "text-emerald-400" },
          { k: "deploying", label: "Deploying", val: stats?.by_status?.deploying ?? 0, color: "text-yellow-400" },
          { k: "down", label: "Down", val: stats?.by_status?.down ?? 0, color: "text-red-400" },
          { k: "secrets", label: "Secrets Set", val: stats?.env_secrets_set ?? 0, color: "gold-text" },
          { k: "domains", label: "Domains Linked", val: stats?.domains_linked ?? 0, color: "gold-text" },
        ].map((s) => (
          <div key={s.k} className="card-sharp p-4" data-testid={`fleet-stat-${s.k}`}>
            <div className="label-tag">{s.label}</div>
            <div className={`text-3xl font-black tracking-tighter mt-1 ${s.color || "text-white"}`}>{s.val}</div>
          </div>
        ))}
      </section>

      {/* CONTROLS */}
      <section className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search hulls, job IDs, or codenames…"
              className="w-full bg-black/50 border border-white/10 pl-10 pr-3 py-2.5 text-sm focus:border-[#C9A227] focus:outline-none rounded-none"
              data-testid="fleet-search-input"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-black/50 border border-white/10 px-3 py-2.5 text-sm text-white focus:border-[#C9A227] focus:outline-none rounded-none"
            data-testid="fleet-status-filter"
          >
            <option value="">All Statuses</option>
            {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <button
            onClick={reseed}
            disabled={reseeding}
            className="btn-ghost-gold inline-flex items-center gap-2 text-xs"
            data-testid="fleet-reseed-btn"
          >
            {reseeding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Re-seed Registry
          </button>
        </div>

        <div className="flex flex-wrap gap-2" data-testid="fleet-category-bar">
          <CategoryChip value="" active={!category} onClick={() => setCategory("")} count={apps.length} />
          {categories.map(([cat, count]) => (
            <CategoryChip
              key={cat}
              value={cat}
              count={count}
              active={category === cat}
              onClick={() => setCategory(category === cat ? "" : cat)}
            />
          ))}
        </div>
      </section>

      {/* TABLE */}
      {loading ? (
        <div className="flex items-center justify-center py-24 text-gray-500" data-testid="fleet-loading">
          <Loader2 className="w-6 h-6 animate-spin mr-3" /> Mustering the fleet…
        </div>
      ) : filtered.length === 0 ? (
        <div className="card-sharp p-12 text-center" data-testid="fleet-empty">
          <Ship className="w-10 h-10 gold-text mx-auto mb-4" strokeWidth={1.2} />
          <p className="text-gray-400">No hulls match these filters.</p>
        </div>
      ) : (
        <div className="card-sharp overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-white/10 text-xs uppercase tracking-widest text-gray-500">
                <th className="px-5 py-4">Hull</th>
                <th className="px-5 py-4">Category</th>
                <th className="px-5 py-4">Subdomain</th>
                <th className="px-5 py-4">Status</th>
                <th className="px-5 py-4 text-center">Checklist</th>
                <th className="px-5 py-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((a) => (
                <tr key={a.id} className="border-b border-white/5 hover:bg-white/5 transition-colors" data-testid={`fleet-row-${a.name}`}>
                  <td className="px-5 py-4">
                    <div className="font-bold">{a.display_name}</div>
                    <div className="text-[10px] text-gray-500 mono">{a.name}</div>
                    <button
                      onClick={() => copyJobId(a.job_id)}
                      className="text-[10px] text-gray-600 hover:gold-text font-mono inline-flex items-center gap-1 mt-1"
                      data-testid={`fleet-copy-job-${a.name}`}
                    >
                      <Copy className="w-3 h-3" /> {a.job_id.slice(0, 8)}…
                    </button>
                  </td>
                  <td className="px-5 py-4 text-gray-300">{a.category}</td>
                  <td className="px-5 py-4">
                    {a.subdomain ? (
                      <span className="font-mono text-xs gold-text">{a.subdomain}</span>
                    ) : (
                      <span className="text-xs text-gray-600">—</span>
                    )}
                  </td>
                  <td className="px-5 py-4"><StatusPill status={a.status} /></td>
                  <td className="px-5 py-4">
                    <div className="flex items-center justify-center gap-3">
                      <span title="Env secrets set" data-testid={`fleet-icon-secrets-${a.name}`}>
                        {a.env_secrets_set
                          ? <KeyRound className="w-3.5 h-3.5 gold-text" />
                          : <KeyRound className="w-3.5 h-3.5 text-gray-700" />}
                      </span>
                      <span title="Domain linked" data-testid={`fleet-icon-domain-${a.name}`}>
                        {a.domain_linked
                          ? <Globe className="w-3.5 h-3.5 gold-text" />
                          : <Globe className="w-3.5 h-3.5 text-gray-700" />}
                      </span>
                      <span title={a.github_repo ? `Journaling to ${a.github_repo}` : "No GitHub repo bound"} data-testid={`fleet-icon-github-${a.name}`}>
                        {a.github_repo
                          ? <Github className="w-3.5 h-3.5 gold-text" />
                          : <Github className="w-3.5 h-3.5 text-gray-700" />}
                      </span>
                      {a.last_journal_at && (
                        <span title={`Last journal: ${new Date(a.last_journal_at).toLocaleString()}`} data-testid={`fleet-icon-journal-${a.name}`}>
                          <BookOpen className="w-3.5 h-3.5 text-emerald-400" />
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-2">
                      {a.production_url && (
                        <a
                          href={a.production_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs label-tag gold-text hover:text-[#E0BC4A] inline-flex items-center gap-1"
                          data-testid={`fleet-open-prod-${a.name}`}
                        >
                          <ExternalLink className="w-3 h-3" /> LIVE
                        </a>
                      )}
                      {a.preview_url && (
                        <a
                          href={a.preview_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs label-tag text-gray-400 hover:text-white inline-flex items-center gap-1"
                          data-testid={`fleet-open-preview-${a.name}`}
                        >
                          <ExternalLink className="w-3 h-3" /> PREV
                        </a>
                      )}
                      <button
                        onClick={() => setEditing(a)}
                        className="text-xs label-tag text-gray-400 hover:gold-text border border-white/10 hover:border-[#C9A227]/40 px-2 py-1"
                        data-testid={`fleet-edit-${a.name}`}
                      >
                        Edit
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* PLATFORM CALLOUT */}
      <section className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="fleet-platform-callouts">
        <div className="card-sharp p-5">
          <RefreshCw className="w-5 h-5 gold-text mb-3" strokeWidth={1.5} />
          <div className="text-sm font-bold mb-1">Redeploys</div>
          <p className="text-xs text-gray-400 leading-relaxed">
            Trigger each redeploy from the hull's own hosting dashboard. There is no cross-app deploy API.
          </p>
        </div>
        <div className="card-sharp p-5">
          <KeyRound className="w-5 h-5 gold-text mb-3" strokeWidth={1.5} />
          <div className="text-sm font-bold mb-1">Secrets</div>
          <p className="text-xs text-gray-400 leading-relaxed">
            Paste Stripe / Airtable keys into each hull's env panel. Mark the checkbox here when done — this console tracks the rollout.
          </p>
        </div>
        <div className="card-sharp p-5">
          <ShieldCheck className="w-5 h-5 gold-text mb-3" strokeWidth={1.5} />
          <div className="text-sm font-bold mb-1">Branding</div>
          <p className="text-xs text-gray-400 leading-relaxed">
            Per-hull branding is set in each hull's own code (favicon, title, meta tags, any badge elements). Mark the checklist when each hull is brand-compliant.
          </p>
        </div>
      </section>

      {editing && (
        <EditDialog
          app={editing}
          onClose={() => setEditing(null)}
          onSaved={(u) => setApps((prev) => prev.map((x) => (x.id === u.id ? u : x)))}
        />
      )}
    </div>
  );
}
